'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import type { SpecialtyOption } from '@/lib/specialties/types';
import { groupLabel } from '@/lib/specialties/groups';
import { searchSpecialties, groupSpecialties, specialtyLabel } from '@/lib/specialties/search';

export interface SpecialtyPickerProps {
  value: string | null; onChange: (id: string | null) => void;
  options: SpecialtyOption[]; exclude?: string[];
  label?: string; placeholder?: string; required?: boolean; error?: string; disabled?: boolean; id?: string;
}

export default function SpecialtyPicker({ value, onChange, options, exclude = [], label, placeholder, required, error, disabled, id }: SpecialtyPickerProps) {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const close = () => { setIsOpen(false); setQuery(''); };

  useEffect(() => {
    if (!isOpen) return;
    searchRef.current?.focus();
    const onDown = (e: MouseEvent) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) close(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen]);

  const visible = useMemo(() => searchSpecialties(options, query, language, exclude), [options, query, language, exclude]);
  const grouped = useMemo(() => (query ? null : groupSpecialties(visible, language)), [visible, query, language]);
  const selected = options.find(o => o.id === value) ?? null;
  const triggerText = selected ? specialtyLabel(selected, language) : (placeholder ?? t('profile.selectSpecialty'));

  const renderOption = (o: SpecialtyOption) => {
    const isSelected = o.id === value;
    return (
      <Button key={o.id} type="button" variant="ghost" role="option" aria-selected={isSelected} data-selected={isSelected}
        onClick={() => { onChange(o.id); close(); }}
        className={`w-full h-auto text-left justify-start rounded-none px-3 sm:px-4 py-2 flex items-start gap-2 text-sm ${isSelected ? 'bg-blue-50 text-blue-700 hover:bg-blue-50' : 'text-gray-900 hover:bg-gray-50 font-normal'}`}>
        <span className="flex-1 min-w-0">
          <span className="block break-words whitespace-normal leading-snug">{specialtyLabel(o, language)}</span>
          <span className="block text-xs text-gray-500 whitespace-normal">{groupLabel(o.groupKey, language)}</span>
        </span>
        {isSelected && <Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />}
      </Button>
    );
  };

  return (
    <div id={id} className="min-w-0" ref={wrapperRef}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1 break-words">{label}{required && <span className="text-red-500">*</span>}</label>}
      <div className="relative">
        <Button type="button" variant="outline" disabled={disabled} onClick={() => { if (disabled) return; if (isOpen) close(); else setIsOpen(true); }}
          aria-haspopup="listbox" aria-expanded={isOpen}
          className={`w-full h-10 text-left justify-start px-4 py-2 pr-12 border ${error ? 'border-red-500' : 'border-gray-300'} bg-white ${value ? 'text-gray-900' : 'text-gray-500'} text-sm font-normal truncate hover:border-gray-400 hover:bg-white`}>
          {triggerText}
        </Button>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-all ${isOpen ? 'rotate-180' : ''}`} />
        </div>
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden" role="listbox">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input ref={searchRef} type="search" role="searchbox" value={query} onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') { if (query) setQuery(''); else close(); } }}
                  placeholder={t('profile.searchSpecialty')} autoComplete="off"
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50" />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {visible.length === 0 && <div className="px-4 py-3 text-sm text-gray-500 text-center">{t('profile.noSpecialtyFound')}</div>}
              {grouped ? grouped.map(g => (
                <div key={g.key}>
                  <div className="px-3 sm:px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50 sticky top-0">{g.label}</div>
                  {g.items.map(renderOption)}
                </div>
              )) : visible.map(renderOption)}
            </div>
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-xs sm:text-sm text-red-600 break-words" role="alert">{error}</p>}
    </div>
  );
}
